import React from 'react';
import * as XLSX from 'xlsx';
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast';

interface SystemItem {
  system?: {
    name?: string;
    status?: string;
    domain?: string;
    reviewer?: {
      name?: string;
      email?: string;
    };
    users?: {
      [key: string]: {
        userId?: string;
        name?: string;
        status?: string;
        approvedAt?: string;
        reviewBy?: {
          name?: string;
          email?: string;
        };
        note?: string;
        remediatedNote?: {
          note?: string;
        };
        reasonForSelfReview?: string;
      };
    };
  };
}

interface ExportReviewToExcelProps {
  filteredItems: SystemItem[];
  reviewName: string;
  disabled?: boolean;
}

const ExportReviewToExcel: React.FC<ExportReviewToExcelProps> = ({ filteredItems, reviewName, disabled }) => {
  const formatDate = (isoDate?: string) => {
    if (!isoDate) return '';
    return isoDate.split('T')[0];
  };

  const handleExport = () => {
    try {
      if (filteredItems.length === 0 || !reviewName) {
        showErrorToast({ message: 'No data to export or review name not found.' });
        return;
      }

      const workbook = XLSX.utils.book_new();

      const summaryData = filteredItems.map((item) => ({
        'System Title': item.system?.name || '',
        'Status': item.system?.status || '',
        'Website': item.system?.domain || '',
        'Monitored System': 'TRUE',
        'Reviewer Name': item.system?.reviewer?.name || '',
        'Reviewer Email': item.system?.reviewer?.email || '',
      }));

      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      summarySheet['!cols'] = [
        { wch: 20 },
        { wch: 15 }, 
        { wch: 30 },
        { wch: 20 }, 
        { wch: 20 },
        { wch: 20 }, 
      ];

      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

      filteredItems.forEach((item) => {
        const systemName = item.system?.name || 'Unknown';
        const systemData = Object.values(item.system?.users || {}).map((user) => ({
          'User Account': user.userId || '',
          'Account Holder': user.name || '',
          'Account Creation Date (UTC)': formatDate(user.approvedAt),
          'Access Valid': '',
          'Access Modification Type': '',
          'Reviewed By': user.reviewBy?.name || '',
          'Reviewer Job Title': '',
          'Review Date (UTC)': '',
          'User Role': '',
          'Account Status': user.status
            ? user.status.charAt(0).toUpperCase() + user.status.slice(1)
            : '',
          'MFA Status': '',
          'Review Note': user.note || '',
          Remediation:user.remediatedNote?.note || '',
          'Action Taken': '',
          'Self-Review Notes': user.reasonForSelfReview || '',
        }));

        const systemSheet = XLSX.utils.json_to_sheet(systemData);
        systemSheet['!cols'] = [
          { wch: 20 }, 
          { wch: 20 }, 
          { wch: 20 },
          { wch: 15 }, 
          { wch: 15 }, 
          { wch: 20 }, 
          { wch: 20 },
          { wch: 20 },
          { wch: 15 }, 
          { wch: 15 }, 
          { wch: 15 }, 
          { wch: 30 }, 
          { wch: 30 },
          { wch: 20 }, 
          { wch: 30 },
        ];

        XLSX.utils.book_append_sheet(workbook, systemSheet, systemName);
      });

      XLSX.writeFile(workbook, `${reviewName}.xlsx`);
      showSuccessToast({ message: 'System review data exported successfully' });
    } catch (error) {
      showErrorToast({ message: 'Failed to export data. Please try again.' });
    }
  };

  return (
    <button
      type="button"
      className="btn btn-sm btn-light fs-6"
      style={{
        height: '40px',
        background: '#fff',
        color: '#000',
        border: '1px solid #C5C5C5',
      }}
      onClick={handleExport}
      disabled={disabled}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#5A5A5A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="icon icon-tabler icons-tabler-outline icon-tabler-download mb-1 me-2"
        style={{ marginRight: '4px' }}
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2 -2v-2" />
        <path d="M7 11l5 5l5 -5" />
        <path d="M12 4l0 12" />
      </svg>
      Export
    </button>
  );
};

export default ExportReviewToExcel;